// Genuine cross-device LWW conflict reapply for `capture_items`.
//
// THE RESIDUAL THIS FIXES (memory put-batch-wedge-prod "INDEPENDENT
// VERIFICATION of PR #798"). After a `failed_precondition` whose authoritative
// VALUE differs from this device's intent (a real cross-device conflict — a
// self-inflicted one is ACKed earlier and never reaches here), the connector
// must re-apply the surveyor's count on top of the authoritative version so the
// server resolves to A-wins (real last-write-wins; ADR-003, docs/buildspec/
// capture/10-api.md §4).
//
// The OLD mechanism — "local UPDATE then re-queue" — silently DROPPED the count
// in the degenerate single-conflict: device A's optimistic local row already
// holds A's intent AND A's optimistic `sync_version` equals the authoritative
// `sync_version` (both bumped from the same base v1). The reapply UPDATE then
// rewrites the SAME quantity + SAME version (a value+version no-op), PowerSync's
// `onlyWhenChanged` trims both columns out of the re-queued op, and the
// degenerate-op drain (connector-dispatch-item-patch.ts) ACKs the now-empty op —
// so A's contractual count vanishes and the server keeps the other device's
// value. (`finalQueued=0 serverQty=99` in the harness GENUINE-CONFLICT repro.)
//
// THE FIX: re-apply via a DIRECT PUT at the authoritative version (mirrors
// `dispatchRehomedIncrement` in connector-dispatch-item-patch.ts), sidestepping
// `onlyWhenChanged` entirely — A's quantity reaches the server every time. The
// backend applies it at the matched version and bumps `sync_version` by exactly
// one (update-item.ts applyVersionGuard), so the reapply is exactly-once.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'

import { CAPTURE_ITEM_INCREMENT_UPDATE_SQL } from '@/capture/client/capture-item-increment-sql'

import { ApiError, apiFetch, isNotFoundError } from '../api'

import { quarantineUnsyncableCount } from './connector-quarantine'
import { canonicalizeForUpload } from './quantity-canonical'
import { _emitUploaded } from './upload-events'

/**
 * The authoritative `capture_items` row carried on a `failed_precondition`
 * body, paired with its (validated numeric) `syncVersion`. `row` is the raw
 * camelCase `CaptureItemRow` so it can be handed straight to
 * `authoritativeMatchesLocalIntent` (the self-inflicted check) without a second
 * parse.
 */
export interface AuthoritativeRow {
  row: Record<string, unknown>
  syncVersion: number
}

/**
 * Pull `err.body.details.authoritative` (a camelCase `CaptureItemRow`) out of a
 * `failed_precondition` error, but ONLY when it carries a numeric `syncVersion`.
 * Returns null otherwise — the caller MUST then throw (never silently ack a
 * malformed conflict, which would be the silent-data-loss bug). Defensive
 * because `err.body` is `unknown`.
 */
export function extractAuthoritativeRow(err: ApiError): AuthoritativeRow | null {
  const body = err.body
  const details =
    typeof body === 'object' && body !== null
      ? (body as Record<string, unknown>)['details']
      : null
  const authoritative =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>)['authoritative']
      : null
  if (typeof authoritative !== 'object' || authoritative === null) return null
  const row = authoritative as Record<string, unknown>
  const syncVersion = row['syncVersion']
  if (typeof syncVersion !== 'number') return null
  return { row, syncVersion }
}

/**
 * Re-apply the surveyor's intent onto an existing server row after a GENUINE
 * cross-device LWW conflict (authoritative VALUE ≠ this device's intent). See
 * the file header for why this is a direct PUT, not a local UPDATE.
 *
 * Bounded reapply-once-then-yield (the LWW contract; preserves the historical
 * `lwwAttempts` bound, now enforced inline):
 *   - PUT A's intent at the conflict's authoritative version. Success → the
 *     count is on the server at A's value (server bumps +1); ACK — A wins.
 *   - A FRESH stale-version conflict (a faster editor bumped again in the
 *     reconnect window) → retry ONCE at the newer authoritative.
 *   - Still conflicting → YIELD: reconcile the local row to the bare
 *     authoritative (the *contended* edit loses the race — NOT a silent drop of
 *     an uncontested count) and ack so the queue drains.
 *   - not_found → the row was hard-deleted between the conflict and the
 *     reapply; PRESERVE the on-device count in the quarantine (never silently
 *     drop) and ack.
 */
export async function reapplyCaptureItemIntent(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  auth: AuthoritativeRow,
): Promise<void> {
  const data = entry.opData ?? {}
  const payload = {
    // Canonicalise so a genuine-conflict reapply of a pre-fix queued draft
    // ("5.") sends "5", not the invalid_argument-rejected "5." (which would
    // re-wedge on the A-wins PUT). See quantity-canonical.ts.
    quantity: canonicalizeForUpload(data['quantity']),
    isZeroConfirmed: data['is_zero_confirmed'] === 1,
    notes: data['notes'] ?? null,
  }

  // attempt 0 → the conflict's authoritative version; attempt 1 → the newer
  // authoritative if attempt 0 itself conflicts (reapply once, then yield).
  let target = auth
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await apiFetch(`/capture/items/${encodeURIComponent(entry.id)}`, {
        method: 'PUT',
        json: {
          quantity: payload.quantity,
          isZeroConfirmed: payload.isZeroConfirmed,
          notes: payload.notes,
          syncVersion: target.syncVersion,
        },
      })
      // A's count reached the server at the authoritative version (real upload),
      // so emit `uploaded` — unlike the old local-UPDATE reapply, which returned
      // without uploading anything.
      _emitUploaded(entry.table, entry.id)
      return
    } catch (err) {
      if (isNotFoundError(err)) {
        // not_found OR a code-less 404 (proxy/CDN 404, no Encore code — H3/#694).
        return quarantineUnsyncableCount(
          entry,
          db,
          err,
          data,
          'powersync_capture_item_lww_reapply_not_found_drop',
        )
      }
      if (!(err instanceof ApiError) || err.code !== 'failed_precondition') {
        throw err
      }
      const next = extractAuthoritativeRow(err)
      if (next === null) throw err // malformed conflict — surface, never ack
      target = next
    }
  }

  // Both reapply attempts lost to a faster concurrent editor — yield per the LWW
  // contract: show the winner locally and ack so the queue drains.
  await reconcileLocalToAuthoritative(db, entry.id, target)
}

/**
 * Overwrite the local `capture_items` mirror with the bare authoritative row
 * (drops the surveyor's contended edit on the yield path). The resulting CRUD
 * op is a no-op against the server — local now equals authoritative — so it
 * drains via the self-inflicted-conflict ACK on the next pass. Mirrors the
 * column mapping of `handleLwwConflict`'s historical second-conflict reconcile.
 */
async function reconcileLocalToAuthoritative(
  db: PowerSyncWriteDb,
  id: string,
  auth: AuthoritativeRow,
): Promise<void> {
  // Reuse the shared increment statement so this — the OTHER production
  // capture_items UPDATE — also snapshots FK context into `_metadata`. Same
  // column/param shape (quantity, is_zero_confirmed, notes, sync_version, id);
  // the json_object reads this still-present row's own immutable FK columns.
  // Without it, a reconcile op whose row was later sync-deleted before it drained
  // would quarantine bare (audit-1 #3). The reconcile writes authoritative values
  // (not N+1), but the snapshot is FK-only so the count values are irrelevant to it.
  await db.execute(CAPTURE_ITEM_INCREMENT_UPDATE_SQL, [
    auth.row['quantity'],
    auth.row['isZeroConfirmed'] === true ? 1 : 0,
    auth.row['notes'] ?? null,
    auth.syncVersion,
    id,
  ])
}
